import { Piece, PieceType } from '../entities/Piece';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class PieceGenerator {
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private bag: PieceType[] = [];
  private nextBag: PieceType[] = [];

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.reset();
  }

  public reset(): void {
    this.bag = [];
    this.nextBag = [];
    this.fillBag();
  }

  // Modern Tetris uses a "bag" system to ensure fair distribution
  // Each bag contains exactly one of each piece type, shuffled
  private fillBag(): void {
    if (this.bag.length === 0) {
      // Move next bag to current bag if available
      if (this.nextBag.length > 0) {
        this.bag = [...this.nextBag];
        this.nextBag = [];
      } else {
        // Generate new bag
        this.bag = this.createShuffledBag();
      }
    }

    // Always keep a next bag ready for preview purposes
    if (this.nextBag.length === 0) {
      this.nextBag = this.createShuffledBag();
    }
  }

  private createShuffledBag(): PieceType[] {
    const bag = Object.values(PieceType);
    
    // Fisher-Yates shuffle
    for (let i = bag.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [bag[i], bag[j]] = [bag[j], bag[i]];
    }
    
    return bag;
  }

  public getNextPiece(): Piece {
    this.fillBag();
    
    const pieceType = this.bag.shift()!;
    return Piece.create(
      pieceType,
      0, // Position will be set by the game
      0,
      this.renderManager,
      this.audioManager
    );
  }

  // Peek at upcoming pieces without removing them from the bag
  public previewNextPieces(count: number = 1): PieceType[] {
    this.fillBag();
    
    const preview: PieceType[] = [];
    
    // Get pieces from current bag first
    const availableInCurrentBag = Math.min(count, this.bag.length);
    for (let i = 0; i < availableInCurrentBag; i++) {
      preview.push(this.bag[i]);
    }
    
    // Get remaining pieces from next bag if needed
    const remainingNeeded = count - availableInCurrentBag;
    if (remainingNeeded > 0) {
      for (let i = 0; i < Math.min(remainingNeeded, this.nextBag.length); i++) {
        preview.push(this.nextBag[i]);
      }
    }
    
    return preview;
  }

  // Get statistics about piece distribution
  public getBagStats(): { current: PieceType[], next: PieceType[], remaining: number } {
    return {
      current: [...this.bag],
      next: [...this.nextBag],
      remaining: this.bag.length
    };
  }

  // Reset with a specific seed for reproducible games (for testing)
  public resetWithSeed(seed: number): void {
    // Simple seeded random function
    let seedValue = seed;
    const seededRandom = () => {
      seedValue = (seedValue * 9301 + 49297) % 233280;
      return seedValue / 233280;
    };
    
    // Override Math.random temporarily
    const originalRandom = Math.random;
    Math.random = seededRandom;
    
    this.reset();
    
    // Restore original Math.random
    Math.random = originalRandom;
  }

  // Generate a specific piece type (useful for testing)
  public generateSpecificPiece(type: PieceType): Piece {
    return Piece.create(
      type,
      0,
      0,
      this.renderManager,
      this.audioManager
    );
  }

  // Get piece frequency statistics (for debugging)
  public getFrequencyStats(): { [key in PieceType]: number } {
    const stats: { [key in PieceType]: number } = {
      [PieceType.I]: 0,
      [PieceType.O]: 0,
      [PieceType.T]: 0,
      [PieceType.S]: 0,
      [PieceType.Z]: 0,
      [PieceType.J]: 0,
      [PieceType.L]: 0
    };

    // Count pieces in current bag
    this.bag.forEach(type => stats[type]++);
    
    // Count pieces in next bag
    this.nextBag.forEach(type => stats[type]++);

    return stats;
  }
}